import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, Plus, ShieldCheck, Trash2, Download, Sparkles, X, FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const KIND_ICON = "ScrollText";
const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export default function Affidavits() {
    const { profile, refreshVaultDocs } = useApp();
    const [templates, setTemplates] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [picking, setPicking] = useState(false);
    const [editing, setEditing] = useState(null); // { template, statements, state, county, typedName }

    const refresh = async () => {
        setLoading(true);
        try {
            const [t, d] = await Promise.all([
                apiGet("/affidavits/templates"),
                apiGet("/vault/documents"),
            ]);
            setTemplates(t.templates || []);
            setDocs((d.items || []).filter(i => i.kind === "affidavit"));
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const startNew = (template) => {
        setPicking(false);
        setEditing({
            template,
            statements: [...(template.statements || [])],
            state: profile.state || "",
            county: "",
            typedName: `${profile.legal_first_name || ""} ${profile.legal_last_name || ""}`.trim(),
            consent: false,
            saving: false,
        });
    };

    const updateStatement = (i, val) => setEditing(s => {
        const next = [...s.statements]; next[i] = val; return { ...s, statements: next };
    });
    const addStatement = () => setEditing(s => ({ ...s, statements: [...s.statements, ""] }));
    const removeStatement = (i) => setEditing(s => {
        const next = [...s.statements]; next.splice(i, 1); return { ...s, statements: next };
    });

    const generate = async () => {
        if (!editing) return;
        if (!editing.consent) return toast.error("You must acknowledge the perjury attestation to sign.");
        if (!editing.typedName.trim()) return toast.error("Please type your full legal name as the affiant.");
        const filtered = editing.statements.map(s => (s || "").trim()).filter(Boolean);
        if (!filtered.length) return toast.error("Please write at least one factual statement.");

        setEditing(s => ({ ...s, saving: true }));
        try {
            await apiPost("/affidavits/generate", {
                kind: editing.template.id,
                statements: filtered,
                state: editing.state,
                county: editing.county,
                typed_name: editing.typedName,
                consent: true,
            });
            toast.success("Affidavit generated and saved to your vault");
            setEditing(null);
            await refresh();
            await refreshVaultDocs();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Couldn't generate affidavit");
            setEditing(s => ({ ...s, saving: false }));
        }
    };

    const downloadDoc = async (doc) => {
        try {
            const r = await api.get(`/vault/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = doc.filename || "affidavit.pdf";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch { toast.error("Couldn't download"); }
    };

    const removeDoc = async (doc) => {
        if (!window.confirm("Delete this affidavit?")) return;
        try {
            await api.delete(`/vault/documents/${doc.id}`);
            toast.success("Removed");
            await refresh();
            await refreshVaultDocs();
        } catch { toast.error("Couldn't delete"); }
    };

    return (
        <div className="px-5 sm:px-8 py-8 lg:py-10 max-w-6xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Sworn statements</p>
                    <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">Affidavits</h1>
                    <p className="mt-3 text-muted-foreground max-w-2xl">Generate notarization-ready sworn affidavits — signed under penalty of perjury, with a built-in notary block. They auto-attach to every packet you sign.</p>
                </div>
                <Button onClick={() => setPicking(true)} className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5" data-testid="affidavits-new-btn">
                    <Plus className="h-4 w-4 mr-1" /> New affidavit
                </Button>
            </div>

            {loading ? (
                <div className="mt-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : docs.length === 0 ? (
                <Card className="mt-10 p-12 text-center border-dashed border-border" data-testid="affidavits-empty">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary mx-auto"><ScrollText className="h-6 w-6 text-muted-foreground" /></span>
                    <h2 className="mt-5 font-serif text-3xl">No affidavits yet</h2>
                    <p className="mt-3 text-muted-foreground max-w-md mx-auto">Pick a template — Identity, Residency, Eligibility, Income, Lost Document, or Custom — and Owed will pre-fill it from your vault.</p>
                    <Button onClick={() => setPicking(true)} className="mt-6 rounded-full bg-foreground text-background hover:bg-foreground/90"><Plus className="h-4 w-4 mr-1" /> Start the first one</Button>
                </Card>
            ) : (
                <div className="mt-10 grid md:grid-cols-2 gap-4">
                    {docs.map(d => (
                        <Card key={d.id} className="p-5 border-border" data-testid={`affidavit-card-${d.id}`}>
                            <div className="flex items-start gap-3">
                                <span className="h-10 w-10 rounded-lg bg-accent/15 text-accent-foreground inline-flex items-center justify-center shrink-0"><ScrollText className="h-5 w-5" /></span>
                                <div className="flex-1 min-w-0">
                                    <p className="font-serif text-lg leading-tight">{d.kind_label || "Affidavit"}</p>
                                    <p className="text-xs text-muted-foreground mt-1 truncate">{d.filename}</p>
                                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-2">{Math.round((d.size||0)/1024)} KB · {d.created_at?.slice(0,10)}</p>
                                </div>
                            </div>
                            <div className="mt-5 flex items-center justify-between gap-2">
                                <Badge variant="outline" className="bg-warning/10 border-warning/30 text-warning text-xs">Get notarized</Badge>
                                <div className="flex items-center gap-1">
                                    <Button onClick={() => downloadDoc(d)} size="sm" variant="ghost" className="rounded-full h-8" data-testid={`affidavit-download-${d.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>
                                    <button onClick={() => removeDoc(d)} className="text-muted-foreground hover:text-destructive p-2" title="Delete" data-testid={`affidavit-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {/* Template picker */}
            <Dialog open={picking} onOpenChange={setPicking}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Pick an affidavit template</DialogTitle>
                        <DialogDescription>Owed pre-fills facts from your vault. You can edit every line before signing.</DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        {templates.map(t => (
                            <button key={t.id} onClick={() => startNew(t)} className="text-left p-4 rounded-xl border border-border hover:border-foreground/40 hover:bg-secondary/40 transition-colors" data-testid={`affidavit-template-${t.id}`}>
                                <div className="flex items-center gap-2">
                                    <ScrollText className="h-4 w-4 text-muted-foreground" />
                                    <p className="font-serif text-base">{t.label}</p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed line-clamp-3">{t.description}</p>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Editor */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    {editing && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-serif text-2xl">{editing.template.label}</DialogTitle>
                                <DialogDescription>{editing.template.description}</DialogDescription>
                            </DialogHeader>
                            <div className="mt-4 grid sm:grid-cols-3 gap-3">
                                <div>
                                    <Label className="text-xs">Affiant (your full legal name)</Label>
                                    <Input value={editing.typedName} onChange={(e) => setEditing(s => ({ ...s, typedName: e.target.value }))} className="mt-1.5 h-10 rounded-xl" data-testid="affidavit-affiant-input" />
                                </div>
                                <div>
                                    <Label className="text-xs">State</Label>
                                    <select value={editing.state} onChange={(e) => setEditing(s => ({ ...s, state: e.target.value }))} className="mt-1.5 w-full h-10 rounded-xl border border-border bg-card text-sm px-3" data-testid="affidavit-state-input">
                                        <option value="">—</option>
                                        {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-xs">County (optional)</Label>
                                    <Input value={editing.county} onChange={(e) => setEditing(s => ({ ...s, county: e.target.value }))} placeholder="e.g., Kings" className="mt-1.5 h-10 rounded-xl" data-testid="affidavit-county-input" />
                                </div>
                            </div>

                            <div className="mt-5">
                                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Sworn factual statements</p>
                                <div className="mt-3 space-y-2">
                                    {editing.statements.map((s, i) => (
                                        <div key={i} className="flex items-start gap-2" data-testid={`affidavit-stmt-row-${i}`}>
                                            <span className="font-serif text-base text-muted-foreground pt-2 w-7 text-right shrink-0">{i + 1}.</span>
                                            <Textarea value={s} onChange={(e) => updateStatement(i, e.target.value)} className="min-h-[60px] rounded-xl text-sm" data-testid={`affidavit-stmt-${i}`} />
                                            <button onClick={() => removeStatement(i)} className="text-muted-foreground hover:text-destructive p-2 shrink-0" title="Remove statement" data-testid={`affidavit-stmt-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                                        </div>
                                    ))}
                                </div>
                                <Button onClick={addStatement} size="sm" variant="ghost" className="mt-2 rounded-full" data-testid="affidavit-stmt-add-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add statement</Button>
                            </div>

                            <div className="mt-5 p-4 rounded-xl bg-secondary/40 border border-border">
                                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Penalty of perjury</p>
                                <p className="text-sm mt-1.5 leading-relaxed">I declare under penalty of perjury under the laws of the United States and the laws of the state above that the foregoing statements are true and correct to the best of my knowledge and belief.</p>
                                <label className="mt-3 flex items-start gap-3 cursor-pointer">
                                    <Checkbox checked={editing.consent} onCheckedChange={(v) => setEditing(s => ({ ...s, consent: !!v }))} data-testid="affidavit-consent-checkbox" />
                                    <span className="text-sm">I am the affiant. I have read every statement above. I sign under penalty of perjury.</span>
                                </label>
                            </div>

                            <div className="mt-2 p-3 rounded-xl bg-warning/5 border border-warning/30">
                                <p className="text-xs text-warning-foreground leading-relaxed flex items-start gap-2">
                                    <ShieldCheck className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                                    <span>The PDF includes a notary block. <strong>Sign in front of a notary</strong> (most banks do it free for customers, ~$10–25 elsewhere) for the affidavit to be legally complete.</span>
                                </p>
                            </div>

                            <div className="mt-5 flex justify-end gap-2">
                                <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
                                <Button onClick={generate} disabled={editing.saving} className="rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="affidavit-generate-btn">
                                    {editing.saving ? "Generating…" : (<><Sparkles className="h-4 w-4 mr-1" /> Generate signed PDF</>)}
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
